import { QUOTA_PRESSURE_RATIO } from '@/shared/ui/components/composed/settings-block';

/** How long cancelled data is kept, in days. One definition, interpolated. */
export const DATA_RETENTION_DAYS = 120;

/** How many consecutive over-quota cycles make a pattern worth naming. */
export const REPEAT_THRESHOLD = 3;

/** One metered quota, as the usage endpoint reports it. */
export interface MeterSource {
  used: number;
  total: number;
}

/** The derived figures the history endpoint adds, all optional. */
export interface MeterHistory {
  projected_exhaustion_date?: string | null;
  days_before_renewal?: number | null;
  daily_average?: number | null;
  consecutive_over_quota_cycles?: number | null;
}

/** What a meter card renders. */
export interface MeterView {
  key: 'interaction' | 'partnershipSearch';
  /** The figure the eye lands on: what is LEFT, never what was spent. */
  remaining: number;
  used: number;
  total: number;
  ratio: number | null;
  /** Token reference; cobre from 90% consumption, petroleo below. */
  tone: string;
  underPressure: boolean;
  /** True while the free-period ceiling sizes this quota (RN-06). */
  trial: boolean;
}

const PETROLEO = 'var(--accent-petroleo)';
const COBRE = 'var(--accent-cobre)';

/**
 * Computes the consumed share of a quota.
 *
 * @param used - Units consumed.
 * @param total - The ceiling, or zero when there is none.
 * @returns The share in [0, 1], or null when there is no ceiling.
 */
export function shareOf(used: number, total: number): number | null {
  if (!total || total <= 0) return null;
  return Math.min(used / total, 1);
}

/**
 * Builds one meter view.
 *
 * The big number is the remainder. "60 left" decides an action; "740 of 800"
 * is a reconciliation and belongs in the small print underneath. Inverting the
 * two turns the card into a report.
 *
 * @param key - Which quota this is.
 * @param source - The reported consumption.
 * @param trial - Whether the free-period ceiling sizes this quota.
 * @returns The view.
 */
export function buildMeter(
  key: MeterView['key'], source: MeterSource, trial = false,
): MeterView {
  const ratio = shareOf(source.used, source.total);
  const underPressure = ratio !== null && ratio >= QUOTA_PRESSURE_RATIO;
  return {
    key,
    remaining: Math.max(source.total - source.used, 0),
    used: source.used,
    total: source.total,
    ratio,
    tone: underPressure ? COBRE : PETROLEO,
    underPressure,
    trial,
  };
}

/** The usage payload the meters are built from. */
export interface UsageSource {
  /** True while the subscription trials: totals are the 200 / 3 ceiling. */
  trial_cap?: boolean;
  interaction: MeterSource;
  partnership_search: MeterSource;
}

/**
 * Builds every meter the plan tab shows.
 *
 * Returns an empty list when usage could not be read: bars drawn against an
 * unknown denominator read as a limit someone can hit.
 *
 * @param usage - The usage summary, or null.
 * @returns The meters, in display order.
 */
export function buildMeters(usage: UsageSource | null): MeterView[] {
  if (!usage) return [];
  const trial = usage.trial_cap === true;
  return [
    buildMeter('interaction', usage.interaction, trial),
    buildMeter('partnershipSearch', usage.partnership_search, trial),
  ];
}

/** Which offer, if any, the data has earned. */
export type Offer =
  | { kind: 'none' }
  | { kind: 'alert'; meter: MeterView; showPack: boolean; showPlan: boolean }
  | { kind: 'invite' };

/** What decideOffer needs to know beyond the meters. */
export interface OfferInput {
  meters: MeterView[];
  /** True when there is a plan above what the agency pays today. */
  hasUpgradeTarget: boolean;
  /** FF_APP_ADDONS; the one-off pack is unreachable when false. */
  packAvailable: boolean;
  /** Cancelled and expired subscriptions are not sold to. */
  subscriptionStatus?: string;
}

const UNSELLABLE = new Set(['cancelled', 'expired']);

/**
 * Whether the account can be offered anything at all.
 *
 * During the trial the ceiling is the free period's, not the plan's: a pack
 * or "upgrade for next cycle" would sell against the wrong limit. The trial
 * notices offer the right exit (activate now) instead.
 *
 * @param input - The meters and the account's circumstances.
 * @returns False with no usage, an unsellable status, or a trial meter.
 */
function canBeOffered({ meters, subscriptionStatus }: OfferInput): boolean {
  if (!meters.length || UNSELLABLE.has(subscriptionStatus ?? '')) return false;
  return !meters.some((meter) => meter.trial);
}

/**
 * Decides which offer block the data warrants, if any.
 *
 * A trial never gets one: its ceiling is not the plan's. An alert needs a
 * quota actually running out. An invite needs somewhere to
 * go. Neither is shown against an account that cannot buy, or one whose usage
 * could not be read — you cannot make a usage argument with no usage.
 *
 * @param input - The meters and the account's circumstances.
 * @returns The offer to render.
 */
export function decideOffer(input: OfferInput): Offer {
  if (!canBeOffered(input)) return { kind: 'none' };
  const pressured = input.meters.find((meter) => meter.underPressure);
  if (pressured) return alertFor(pressured, input);
  return input.hasUpgradeTarget ? { kind: 'invite' } : { kind: 'none' };
}

/**
 * The alert for a quota running out, when there is something to sell.
 *
 * @param meter - The meter under pressure.
 * @param input - What the account can buy.
 * @returns The alert, or no offer when neither pack nor plan is available.
 */
function alertFor(meter: MeterView, { hasUpgradeTarget, packAvailable }: OfferInput): Offer {
  if (!packAvailable && !hasUpgradeTarget) return { kind: 'none' };
  return { kind: 'alert', meter, showPack: packAvailable, showPlan: hasUpgradeTarget };
}

/**
 * Whether the repeat-offender sentence has earned its place.
 *
 * Below the threshold the sentence is omitted entirely rather than rendered
 * with a small number — "it is the 1st cycle in a row" is not an argument.
 *
 * @param cycles - The consecutive over-quota count, when known.
 * @returns True when the sentence should render.
 */
export function shouldNameTheStreak(cycles: number | null | undefined): boolean {
  return typeof cycles === 'number' && cycles >= REPEAT_THRESHOLD;
}
